import { Button } from '@spiaggeit/spit-ui'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { Service } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import { serviceQuantitySelector } from '@/store/cartSlice/selectors/serviceQuantitySelector'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { timeScheduleSlice } from '@/store/timeScheduleSlice'
import { TimeScheduleDialog } from '.'

type Props = {
  ticket: Service
}

export function TimeScheduleDialogTrigger(props: Props) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const quantity = useAppSelector((state) =>
    serviceQuantitySelector(state, props.ticket)
  )
  const service = useAppSelector(timeScheduleSlice.selectors.service)

  const isEdit = quantity !== 0

  const triggerLabel = useMemo(() => {
    if (dayDifference === 1) {
      return t(
        isEdit
          ? 'timeSchedulesDialog.triggerEditSingleDay'
          : 'timeSchedulesDialog.triggerSelectSingleDay'
      )
    }

    return t(
      isEdit
        ? 'timeSchedulesDialog.triggerEdit'
        : 'timeSchedulesDialog.triggerSelect'
    )
  }, [isEdit, dayDifference])

  function onTriggerClick() {
    if (isEdit) {
      const ticketReservation = cartReservations.find(
        (r) => r.spotName === null && r.spotType === null
      )
      if (ticketReservation?.boughtServices[props.ticket.serviceId]) {
        const boughtServices =
          ticketReservation.boughtServices[props.ticket.serviceId]

        const days = new Set<number>()
        Object.values(boughtServices).forEach((boughtService) => {
          boughtService.days.forEach((day) => {
            const isoDate = DateTime.fromSeconds(day, {
              locale: 'utc',
            }).toISODate()
            days.add(DateTime.fromISO(isoDate).toMillis())
          })
        })
        dispatch(timeScheduleSlice.actions.setSelectedDays(Array.from(days)))

        Object.values(boughtServices).forEach((boughtService) => {
          dispatch(
            timeScheduleSlice.actions.changeTimeScheduleAmount({
              amount: boughtService.bought,
              timeSchedule: boughtService.timeSchedule,
            })
          )
        })
      }
    }
    dispatch(timeScheduleSlice.actions.setService(props.ticket))
  }

  return (
    <>
      <Button
        className="grow rounded"
        onClick={onTriggerClick}
        type="button"
        variant="outline"
      >
        {triggerLabel}
      </Button>

      {service?.serviceId === props.ticket.serviceId ? (
        <TimeScheduleDialog
          {...props}
          onClose={() => dispatch(timeScheduleSlice.actions.reset())}
        />
      ) : null}
    </>
  )
}
